<script lang="ts">
  import { app } from "../../state/app.svelte";
  import { uploadFiles } from "../../state/uploads";
  import { readDroppedEntries } from "../../state/dropEntries";
  import { tokenLimitsFor } from "../../state/config";
  import type { StoredDocument } from "../../db/types";
  import { i18n } from "../../i18n/i18n.svelte";

  const fr = {
    rejected: (n: number, formats: string) =>
      `${n} fichier${n > 1 ? "s" : ""} ignoré${n > 1 ? "s" : ""} (format non supporté). Formats acceptés : ${formats}.`,
    deleteDoc: (name: string) =>
      `Supprimer "${name}" ? Les conversations qui le référencent resteront dans l'historique.`,
    deleteSelection: (n: number) =>
      `Supprimer ${n} élément${n > 1 ? "s" : ""} (dossiers et/ou documents sélectionnés) ? Les dossiers et leurs ` +
      `documents seront supprimés définitivement ; les conversations qui les référencent resteront dans l'historique.`,
    newFolderPrompt: "Nom du dossier (un sous-dossier s'écrit « Parent/Enfant ») :",
    deleteFolderWithDocs: (path: string, n: number) =>
      `Supprimer le dossier "${path}" et ses ${n} document${n > 1 ? "s" : ""} ? Cette action est définitive.`,
    deleteFolderEmpty: (path: string) => `Supprimer le dossier "${path}" ?`,
    nDocuments: (n: number) => `${n} documents`,
    document: "document",
    libraryAria: "Bibliothèque de documents",
    addDocs: "+ Ajouter des documents",
    newFolderTitle: "Créer un dossier",
    newFolder: "+ Dossier",
    dropHint: "ou glisser-déposer fichiers / dossiers n'importe où, ou sur un dossier",
    dropToRoot: "Déposer à la racine, ou sur un dossier pour l'y ranger",
    dropToFolder: (path: string) => `Déposer dans 📁 ${path}`,
    queued: "en attente",
    parsing: "parsing + embedding…",
    done: "✓ terminé",
    empty: "Aucun document. Ajoute un DPA, SOC 2 ou protocole pour commencer.",
    selectAll: "Tout sélectionner",
    deselectAll: "Tout désélectionner",
    deleteSelectionBtn: (n: number) => `Supprimer la sélection${n > 0 ? ` (${n})` : ""}`,
    moveAria: "Déplacer la sélection vers un dossier",
    moveTo: "Déplacer vers…",
    root: "(racine)",
    fullTextLabel:
      "Mode FullText — envoyer l'intégralité des documents (et non plus seulement les passages pertinents)",
    sizeSoft:
      "⚠ Beaucoup de texte sélectionné. La réponse sera lente. Si elle échoue avec une erreur de contexte, désactive quelques documents.",
    sizeHard:
      "⚠ Volume probablement supérieur à la fenêtre du modèle. Désactive des documents avant d'envoyer ta question.",
    dragHintFolder: "Glisser sur un dossier pour l'y imbriquer, ou ici pour la racine.",
    dragHintDoc: "Glisser sur un dossier pour l'y ranger, ou ici pour la racine.",
    dragFolder: "Glisser pour déplacer le dossier",
    expand: "Déplier",
    collapse: "Replier",
    selectFolder: "Sélectionner tout le dossier",
    deleteFolder: "Supprimer le dossier",
    dragDoc: "Glisser pour ranger dans un dossier",
    delete: "Supprimer",
  };
  const en: typeof fr = {
    rejected: (n, formats) =>
      `${n} file${n > 1 ? "s" : ""} skipped (unsupported format). Accepted formats: ${formats}.`,
    deleteDoc: (name) =>
      `Delete "${name}"? Conversations that reference it will stay in the history.`,
    deleteSelection: (n) =>
      `Delete ${n} item${n > 1 ? "s" : ""} (selected folders and/or documents)? Folders and their documents ` +
      `will be deleted for good; conversations that reference them will stay in the history.`,
    newFolderPrompt: "Folder name (write a subfolder as “Parent/Child”):",
    deleteFolderWithDocs: (path, n) =>
      `Delete the folder "${path}" and its ${n} document${n > 1 ? "s" : ""}? This cannot be undone.`,
    deleteFolderEmpty: (path) => `Delete the folder "${path}"?`,
    nDocuments: (n) => `${n} documents`,
    document: "document",
    libraryAria: "Document library",
    addDocs: "+ Add documents",
    newFolderTitle: "Create a folder",
    newFolder: "+ Folder",
    dropHint: "or drag and drop files / folders anywhere, or onto a folder",
    dropToRoot: "Drop at the root, or onto a folder to file them there",
    dropToFolder: (path) => `Drop into 📁 ${path}`,
    queued: "queued",
    parsing: "parsing + embedding…",
    done: "✓ done",
    empty: "No documents. Add a DPA, a SOC 2 report or a protocol to get started.",
    selectAll: "Select all",
    deselectAll: "Deselect all",
    deleteSelectionBtn: (n) => `Delete selection${n > 0 ? ` (${n})` : ""}`,
    moveAria: "Move the selection to a folder",
    moveTo: "Move to…",
    root: "(root)",
    fullTextLabel:
      "FullText mode — send the documents in full (rather than only the relevant passages)",
    sizeSoft:
      "⚠ A lot of text selected. The answer will be slow. If it fails with a context error, deactivate a few documents.",
    sizeHard:
      "⚠ The volume probably exceeds the model's context window. Deactivate some documents before sending your question.",
    dragHintFolder: "Drag onto a folder to nest it there, or here for the root.",
    dragHintDoc: "Drag onto a folder to file it there, or here for the root.",
    dragFolder: "Drag to move the folder",
    expand: "Expand",
    collapse: "Collapse",
    selectFolder: "Select the whole folder",
    deleteFolder: "Delete the folder",
    dragDoc: "Drag to file into a folder",
    delete: "Delete",
  };
  const t = $derived(i18n.locale === "en" ? en : fr);

  let fileInput: HTMLInputElement;
  let isDragging = $state(false);
  let dragDepth = 0;
  // Folder under the pointer while files from the OS are dragged over the
  // library: the drop lands there (null = root). Found from the event target,
  // since OS file drags do deliver dragover/drop events in the side panel —
  // only in-app `draggable` drags are broken there.
  let fileDropFolder = $state<string | null>(null);

  const ACCEPTED_EXT = [
    "pdf", "docx", "doc", "odt", "xlsx", "xls", "ods", "csv", "tsv", "html", "htm", "txt", "md",
  ];

  function isAccepted(file: File): boolean {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    return ACCEPTED_EXT.includes(ext);
  }

  async function onFilesPicked(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    if (!input.files) return;
    const files = Array.from(input.files);
    input.value = "";
    // Button/picker uploads land at root; the user can move them afterwards.
    await uploadFiles(files.map((file) => ({ file, folder: null })));
  }

  function onDragEnter(e: DragEvent) {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    dragDepth++;
    isDragging = true;
  }

  function onDragLeave(e: DragEvent) {
    e.preventDefault();
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) {
      isDragging = false;
      fileDropFolder = null;
    }
  }

  function folderAt(target: EventTarget | null): string | null {
    const el = target instanceof Element ? target.closest("[data-folder]") : null;
    return el?.getAttribute("data-folder") ?? null;
  }

  function onDragOver(e: DragEvent) {
    if (!e.dataTransfer?.types.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    fileDropFolder = folderAt(e.target);
  }

  /** `sub` placed under `base`; either may be null (root). */
  function joinFolder(base: string | null, sub: string | null): string | null {
    if (!base) return sub;
    return sub ? `${base}/${sub}` : base;
  }

  async function onDrop(e: DragEvent) {
    e.preventDefault();
    const base = folderAt(e.target);
    dragDepth = 0;
    isDragging = false;
    fileDropFolder = null;
    if (!e.dataTransfer) return;
    // Read folders recursively (preserves each file's sub-path). Must run
    // before any await consumes the DataTransfer — readDroppedEntries captures
    // the entries synchronously internally.
    const dropped = await readDroppedEntries(e.dataTransfer);
    if (dropped.length === 0) return;
    const accepted = dropped.filter((d) => isAccepted(d.file));
    const rejected = dropped.length - accepted.length;
    if (rejected > 0) {
      alert(t.rejected(rejected, ACCEPTED_EXT.join(", ")));
    }
    if (accepted.length > 0) {
      // A dropped directory keeps its structure, rebuilt under the folder the
      // drop landed on.
      await uploadFiles(
        accepted.map(({ file, relPath }) => {
          const slash = relPath.lastIndexOf("/");
          return {
            file,
            folder: joinFolder(base, slash === -1 ? null : relPath.slice(0, slash)),
          };
        }),
      );
    }
  }

  async function onDelete(docId: string, name: string) {
    if (!confirm(t.deleteDoc(name))) {
      return;
    }
    await app.removeDoc(docId);
  }

  function toggleSelectAll() {
    const allOn =
      app.activeDocIds.length === app.docs.length &&
      app.selectedFolders.length === app.allFolders.length;
    if (allOn) {
      app.setActiveDocs([]);
      app.selectedFolders = [];
    } else {
      app.setActiveDocs(app.docs.map((d) => d.id));
      app.selectedFolders = [...app.allFolders];
    }
  }

  async function onDeleteSelected() {
    const folders = [...app.selectedFolders];
    if (folders.length === 0 && app.activeDocIds.length === 0) return;
    const n = selectionCount;
    if (!confirm(t.deleteSelection(n))) {
      return;
    }
    // Delete folders first — this removes their documents and prunes them from
    // the active set; whatever loose documents remain selected go next.
    for (const folder of folders) {
      await app.deleteFolder(folder);
    }
    const remaining = [...app.activeDocIds];
    if (remaining.length > 0) await app.removeDocs(remaining);
  }

  // ---- Folders -------------------------------------------------------------

  // A folder is shown collapsed only if its path is in this set (so everything
  // is expanded by default — good for discoverability with few folders).
  let collapsed = $state(new Set<string>());

  function toggleCollapsed(path: string) {
    const next = new Set(collapsed);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    collapsed = next;
  }

  async function onNewFolder() {
    const name = prompt(t.newFolderPrompt);
    if (name) await app.createFolder(name);
  }

  async function onDeleteFolder(path: string) {
    const docCount = app.docsInFolder(path, true).length;
    const msg = docCount > 0
      ? t.deleteFolderWithDocs(path, docCount)
      : t.deleteFolderEmpty(path);
    if (!confirm(msg)) return;
    await app.deleteFolder(path);
  }

  async function onMoveSelection(e: Event) {
    const select = e.currentTarget as HTMLSelectElement;
    const value = select.value;
    if (value === "") return; // placeholder
    const folder = value === "__root__" ? null : value;
    await app.moveDocsToFolder([...app.activeDocIds], folder);
    select.value = "";
  }

  /** Immediate sub-folders of `parent` (null = top level). */
  function childFolders(parent: string | null): string[] {
    return app.allFolders.filter((f) => {
      const slash = f.lastIndexOf("/");
      const fParent = slash === -1 ? null : f.slice(0, slash);
      return fParent === parent;
    });
  }

  /** Selection state of a folder header checkbox: ticked when the folder is
   *  explicitly selected (so empty folders can be selected too) or when all of
   *  its documents are active. */
  function folderSelection(path: string): { checked: boolean; indeterminate: boolean } {
    if (app.selectedFolders.includes(path)) return { checked: true, indeterminate: false };
    const ids = app.docsInFolder(path, true).map((d) => d.id);
    if (ids.length === 0) return { checked: false, indeterminate: false };
    const active = new Set(app.activeDocIds);
    const n = ids.filter((id) => active.has(id)).length;
    return { checked: n === ids.length, indeterminate: n > 0 && n < ids.length };
  }

  /** Number of distinct things the bulk bar would act on: every selected folder
   *  plus every selected document not already inside a selected folder. */
  const selectionCount = $derived.by(() => {
    const inFolders = new Set(
      app.selectedFolders.flatMap((p) => app.docsInFolder(p, true).map((d) => d.id)),
    );
    const looseDocs = app.activeDocIds.filter((id) => !inFolders.has(id)).length;
    return looseDocs + app.selectedFolders.length;
  });

  // `indeterminate` is a DOM property, not an attribute — set it imperatively.
  function indeterminate(node: HTMLInputElement, value: boolean) {
    node.indeterminate = value;
    return {
      update(v: boolean) {
        node.indeterminate = v;
      },
    };
  }

  // ---- Reorganize: pointer-based drag & drop -------------------------------
  //
  // Native HTML5 drag-and-drop (draggable="true") does not work reliably inside
  // the extension side-panel / webview, so reorganizing is implemented with
  // pointer events: grab a grip handle, move, and drop onto a folder (or the
  // root area). Hit-testing uses document.elementFromPoint + data attributes.

  // The in-flight drag. `kind` distinguishes a document move from a folder move.
  let drag = $state<
    | { kind: "doc"; ids: string[]; label: string; x: number; y: number }
    | { kind: "folder"; folder: string; label: string; x: number; y: number }
    | null
  >(null);
  // Highlighted drop target while dragging: { folder: null } = root.
  let dropTarget = $state<{ folder: string | null } | null>(null);
  // Set on pointerdown; promoted to a real drag once the pointer moves enough.
  let pendingDrag:
    | { kind: "doc" | "folder"; id: string; x: number; y: number }
    | null = null;
  const DRAG_THRESHOLD = 4;

  /** Drag the whole active selection when the grabbed doc is part of a
   *  multi-selection; otherwise just the grabbed doc. */
  function docIdsForDrag(docId: string): string[] {
    const active = app.activeDocIds;
    return active.includes(docId) && active.length > 1 ? [...active] : [docId];
  }

  function folderBasename(path: string): string {
    return path.slice(path.lastIndexOf("/") + 1);
  }

  /** A folder can't be dropped into itself or into any of its own descendants
   *  (that would create a cycle). Root (null) is always a valid target. */
  function canDropFolderInto(src: string, destParent: string | null): boolean {
    if (destParent === null) return true;
    return destParent !== src && !destParent.startsWith(src + "/");
  }

  function onGripPointerDown(e: PointerEvent, kind: "doc" | "folder", id: string) {
    if (e.button !== 0) return; // left button only
    e.preventDefault();
    e.stopPropagation();
    pendingDrag = { kind, id, x: e.clientX, y: e.clientY };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }

  function beginDrag(x: number, y: number) {
    if (!pendingDrag) return;
    if (pendingDrag.kind === "doc") {
      const ids = docIdsForDrag(pendingDrag.id);
      const label =
        ids.length > 1
          ? t.nDocuments(ids.length)
          : (app.docs.find((d) => d.id === pendingDrag!.id)?.name ?? t.document);
      drag = { kind: "doc", ids, label, x, y };
    } else {
      drag = {
        kind: "folder",
        folder: pendingDrag.id,
        label: folderBasename(pendingDrag.id),
        x,
        y,
      };
    }
  }

  function updateDropTarget(x: number, y: number) {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    if (!el) {
      dropTarget = null;
      return;
    }
    const folderEl = el.closest("[data-folder]") as HTMLElement | null;
    if (folderEl) {
      const path = folderEl.getAttribute("data-folder") ?? "";
      if (drag?.kind === "folder" && !canDropFolderInto(drag.folder, path)) {
        dropTarget = null;
        return;
      }
      dropTarget = { folder: path };
      return;
    }
    // Inside the tree but not over a folder → root.
    dropTarget = el.closest("[data-tree-root]") ? { folder: null } : null;
  }

  function onPointerMove(e: PointerEvent) {
    if (!pendingDrag) return;
    if (!drag) {
      const moved = Math.hypot(e.clientX - pendingDrag.x, e.clientY - pendingDrag.y);
      if (moved < DRAG_THRESHOLD) return;
      beginDrag(e.clientX, e.clientY);
    }
    if (!drag) return;
    drag = { ...drag, x: e.clientX, y: e.clientY };
    updateDropTarget(e.clientX, e.clientY);
  }

  async function onPointerUp() {
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    const d = drag;
    const target = dropTarget;
    pendingDrag = null;
    drag = null;
    dropTarget = null;
    if (!d || !target) return;
    if (d.kind === "doc") {
      await app.moveDocsToFolder(d.ids, target.folder);
    } else if (target.folder === null) {
      await app.renameFolder(d.folder, folderBasename(d.folder));
    } else if (canDropFolderInto(d.folder, target.folder)) {
      await app.renameFolder(d.folder, `${target.folder}/${folderBasename(d.folder)}`);
    }
  }

  // Rough char→token heuristic (fr/en mix ≈ 3.5-4 chars/token). Used only to
  // give a ballpark warning before the LLM call — not exact.
  const approxTokens = $derived(
    app.bypassRag ? Math.round(app.activeDocsCharCount / 4) : 0,
  );
  // Les deux profils partagent le même modèle depuis le 22.09 : la fenêtre ne
  // dépend donc plus du profil choisi. Cf. tokenLimitsFor dans config.
  const tokenLimits = $derived(tokenLimitsFor(app.config?.rcpModel ?? ""));
  const sizeWarn = $derived(
    approxTokens >= tokenLimits.hard
      ? "hard"
      : approxTokens >= tokenLimits.soft
        ? "soft"
        : null,
  );

  function formatTokens(n: number): string {
    if (n < 1000) return `${n}`;
    return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)} k`;
  }
</script>

<section
  class="library"
  class:dragging={isDragging}
  ondragenter={onDragEnter}
  ondragleave={onDragLeave}
  ondragover={onDragOver}
  ondrop={onDrop}
  role="region"
  aria-label={t.libraryAria}
>
  <div class="actions">
    <button class="upload" onclick={() => fileInput.click()}>
      {t.addDocs}
    </button>
    <button class="new-folder" onclick={onNewFolder} title={t.newFolderTitle}>
      {t.newFolder}
    </button>
    <span class="drop-hint">{t.dropHint}</span>
    <input
      bind:this={fileInput}
      type="file"
      multiple
      accept=".pdf,.docx,.doc,.odt,.xlsx,.xls,.ods,.csv,.tsv,.html,.htm,.txt,.md"
      onchange={onFilesPicked}
      hidden
    />
  </div>

  <!-- A banner, not a full overlay: the folder tree must stay visible for the
       user to aim at a folder. It names where the drop will land. -->
  {#if isDragging}
    <div class="drop-banner" aria-live="polite">
      {fileDropFolder ? t.dropToFolder(fileDropFolder) : t.dropToRoot}
    </div>
  {/if}

  {#if app.uploads.length > 0}
    <ul class="uploads">
      {#each app.uploads as u (u.id)}
        <li class="upload-item {u.status}">
          <span class="filename">{u.filename}</span>
          <span class="status-label">
            {#if u.status === "queued"}{t.queued}
            {:else if u.status === "parsing"}{u.detail ?? t.parsing}
            {:else if u.status === "done"}{t.done}
            {:else if u.status === "error"}✗ {u.error}
            {/if}
          </span>
        </li>
      {/each}
    </ul>
  {/if}

  {#if app.docs.length === 0 && app.allFolders.length === 0}
    <p class="empty">{t.empty}</p>
  {:else}
    <div class="bulk">
      <button type="button" class="bulk-btn" onclick={toggleSelectAll}>
        {app.activeDocIds.length === app.docs.length && app.docs.length > 0 && app.selectedFolders.length === app.allFolders.length ? t.deselectAll : t.selectAll}
      </button>
      <button
        type="button"
        class="bulk-btn danger"
        onclick={onDeleteSelected}
        disabled={selectionCount === 0}
      >{t.deleteSelectionBtn(selectionCount)}</button>
      {#if app.activeDocIds.length > 0 && app.allFolders.length > 0}
        <select class="move-select" onchange={onMoveSelection} aria-label={t.moveAria}>
          <option value="">{t.moveTo}</option>
          <option value="__root__">{t.root}</option>
          {#each app.allFolders as f}
            <option value={f}>{f}</option>
          {/each}
        </select>
      {/if}
    </div>

    <label class="bypass-row tooltip-wrap">
      <input
        type="checkbox"
        bind:checked={app.bypassRag}
        disabled={app.activeDocIds.length === 0}
      />
      <span class="bypass-label">
        {t.fullTextLabel}
        <span class="info-icon" aria-hidden="true">ⓘ</span>
      </span>
      {#if app.bypassRag && app.activeDocIds.length > 0}
        <span class="size-estimate" class:warn-soft={sizeWarn === "soft"} class:warn-hard={sizeWarn === "hard"}>
          ≈ {formatTokens(approxTokens)} tokens
        </span>
      {/if}
      <span class="tooltip" role="tooltip">
        {#if i18n.locale === "en"}
        <strong>By default (mode off):</strong> the system does not read the whole document. It automatically picks the passages most relevant to your question and sends only those to the chatbot. Fast and economical, ideal for a targeted question.<br /><br />
        <strong>In FullText mode (on):</strong> the ticked documents are sent to the chatbot in full, with no prior selection.<br /><br />
        <strong>When to turn it on?</strong> When answering requires knowing the whole document: comparing several documents (“which of these 20 contracts is the most protective?”), writing a summary, or spotting what is missing. In those cases, a few passages are not enough.<br /><br />
        <strong>When to leave it off?</strong> When the question is about a specific point (“what does the termination clause say?”). No need to send the whole document: the passage selection answers better, faster and cheaper.<br /><br />
        <strong>Limit.</strong> If the volume exceeds the chatbot's capacity (1,000,000 tokens), the request may fail or silently ignore part of the text. Well before that limit, answers slow down noticeably: above 250,000 tokens, just reading the context takes about twenty seconds. Deactivate a few documents if the token estimate is high.
        {:else}
        <strong>Par défaut (mode désactivé) :</strong> le système ne lit pas tout le document. Il sélectionne automatiquement les passages les plus pertinents pour répondre à votre question, puis transmet uniquement ceux-ci au chatbot. Rapide et économe, idéal pour une question ciblée.<br /><br />
        <strong>En mode FullText (activé) :</strong> l'intégralité des documents cochés est transmise au chatbot, sans sélection préalable.<br /><br />
        <strong>Quand l'activer ?</strong> Lorsque répondre nécessite de connaître tout le document : comparer plusieurs documents entre eux (« lequel des 20 contrats est le plus protecteur ? »), faire une synthèse, ou repérer ce qui manque. Dans ces cas, quelques passages ne suffisent pas.<br /><br />
        <strong>Quand le laisser désactivé ?</strong> Lorsque la question porte sur un point précis (« que dit la clause de résiliation ? »). Inutile d'envoyer tout le document : la sélection de passages répond mieux, plus vite et moins cher.<br /><br />
        <strong>Limite.</strong> Si le volume dépasse la capacité du chatbot (1 000 000 de tokens), la requête peut échouer ou ignorer silencieusement une partie du texte. Bien avant cette limite, les réponses ralentissent nettement : au-delà de 250 000 tokens, la seule lecture du contexte prend une vingtaine de secondes. Désactivez quelques documents si l'estimation de tokens est élevée.
        {/if}
      </span>
    </label>
    {#if app.bypassRag && sizeWarn === "soft"}
      <p class="size-warn soft">{t.sizeSoft}</p>
    {:else if app.bypassRag && sizeWarn === "hard"}
      <p class="size-warn hard">{t.sizeHard}</p>
    {/if}
    <div
      class="doc-tree"
      class:root-drop={drag && dropTarget?.folder === null}
      data-tree-root
      role="group"
    >
      {#if drag}
        <p class="drag-hint">
          {drag.kind === "folder" ? t.dragHintFolder : t.dragHintDoc}
        </p>
      {/if}
      {#each childFolders(null) as folder (folder)}
        {@render folderNode(folder)}
      {/each}
      {#each app.docsInFolder(null, false) as doc (doc.id)}
        {@render docRow(doc)}
      {/each}
    </div>
  {/if}
</section>

{#if drag}
  <div class="drag-ghost" style="left: {drag.x}px; top: {drag.y}px;">
    {drag.kind === "folder" ? "📁" : "📄"}
    {drag.label}
  </div>
{/if}

{#snippet folderNode(path: string)}
  {@const sel = folderSelection(path)}
  {@const isCollapsed = collapsed.has(path)}
  {@const name = path.slice(path.lastIndexOf("/") + 1)}
  {@const count = app.docsInFolder(path, true).length}
  <div
    class="folder"
    class:drop-target={(drag && dropTarget?.folder === path) ||
      (isDragging && fileDropFolder === path)}
    class:dragging-src={drag?.kind === "folder" && drag.folder === path}
    data-folder={path}
    role="group"
  >
    <div class="folder-row" role="listitem">
      <span
        class="grip"
        title={t.dragFolder}
        onpointerdown={(e) => onGripPointerDown(e, "folder", path)}
        role="listitem"
      >⠿</span>
      <button
        type="button"
        class="chevron"
        onclick={() => toggleCollapsed(path)}
        aria-label={isCollapsed ? t.expand : t.collapse}
      >{isCollapsed ? "▸" : "▾"}</button>
      <input
        type="checkbox"
        checked={sel.checked}
        use:indeterminate={sel.indeterminate}
        onchange={() => app.toggleActiveFolder(path)}
        title={t.selectFolder}
      />
      <button type="button" class="folder-name" onclick={() => toggleCollapsed(path)}>
        📁 {name}
        <span class="folder-count">{count}</span>
      </button>
      <button class="delete" title={t.deleteFolder} onclick={() => onDeleteFolder(path)} aria-label={t.deleteFolder}>×</button>
    </div>
    {#if !isCollapsed}
      <div class="folder-children">
        {#each childFolders(path) as child (child)}
          {@render folderNode(child)}
        {/each}
        {#each app.docsInFolder(path, false) as doc (doc.id)}
          {@render docRow(doc)}
        {/each}
      </div>
    {/if}
  </div>
{/snippet}

{#snippet docRow(doc: StoredDocument)}
  {@const active = app.activeDocIds.includes(doc.id)}
  <div
    class="doc-row-wrap"
    class:active
    class:dragging={drag?.kind === "doc" && drag.ids.includes(doc.id)}
    role="listitem"
  >
    <span
      class="grip"
      title={t.dragDoc}
      onpointerdown={(e) => onGripPointerDown(e, "doc", doc.id)}
      role="listitem"
    >⠿</span>
    <label class="doc-row">
      <input
        type="checkbox"
        checked={active}
        onchange={() => app.toggleActiveDoc(doc.id)}
      />
      <div class="doc-info">
        <div class="doc-name">{doc.name}</div>
        <div class="doc-meta">
          {doc.nChunks} chunks · {doc.parsedBy}{doc.ocrUsed ? " · OCR" : ""}
        </div>
      </div>
    </label>
    <button class="delete" title={t.delete} onclick={() => onDelete(doc.id, doc.name)} aria-label={t.delete}>×</button>
  </div>
{/snippet}

<style>
  .library {
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1;
    min-height: 100%;
    position: relative;
    border: 2px dashed transparent;
    transition: border-color 0.12s ease, background 0.12s ease;
  }

  .library.dragging {
    border-color: var(--epfl-accent);
    background: var(--epfl-gray-100);
  }

  /* Pinned to the bottom of the panel so it never hides the folder being
     aimed at; pointer-events: none keeps the drop going to what is under it. */
  .drop-banner {
    position: fixed;
    left: 50%;
    bottom: 1rem;
    transform: translateX(-50%);
    max-width: calc(100% - 2rem);
    padding: 0.6rem 1rem;
    background: var(--epfl-accent);
    color: var(--epfl-white);
    border-radius: var(--epfl-radius-lg);
    font-size: 0.85rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-shadow: 0 4px 12px rgba(0, 116, 128, 0.25);
    pointer-events: none;
    z-index: 10;
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  /* Deliberately NOT flex:1. Stretched full-bleed, a filled accent stops reading as a
     button and becomes a coloured banner — tolerable in the ~400px side panel, far
     too loud across a full-width web page. Elements' own .btn-primary is
     inline-block, so auto width is also the on-brand shape. */
  .upload {
    padding: 0.6rem 0.9rem;
    background: var(--epfl-accent);
    color: var(--epfl-white);
    border: none;
    border-radius: var(--epfl-radius);
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
    font-family: inherit;
  }

  .upload:hover {
    background: var(--epfl-accent-dark);
  }

  .upload:active:not(:disabled) {
    background: var(--epfl-accent-dark);
  }

  .new-folder {
    padding: 0.6rem 0.7rem;
    background: var(--epfl-gray-50);
    color: var(--epfl-gray-700);
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    font-family: inherit;
    white-space: nowrap;
  }

  .new-folder:hover {
    background: var(--epfl-gray-100);
  }

  /* Was inside the upload label, which is what made the button so wide. */
  .drop-hint {
    color: var(--epfl-gray-600);
    font-size: 0.8rem;
  }

  .uploads {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .upload-item {
    display: flex;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.45rem 0.6rem;
    background: var(--epfl-gray-50);
    border-radius: var(--epfl-radius);
    font-size: 0.75rem;
  }

  .upload-item .filename {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--epfl-text);
  }

  .upload-item .status-label {
    color: var(--epfl-gray-600);
    white-space: nowrap;
  }

  .upload-item.error {
    background: var(--epfl-danger-bg);
  }

  .upload-item.error .status-label {
    color: var(--epfl-danger-text);
  }

  .upload-item.done .status-label {
    color: var(--epfl-success-text);
  }

  .bulk {
    display: flex;
    gap: 0.4rem;
  }

  .bulk-btn {
    padding: 0.3rem 0.6rem;
    background: var(--epfl-gray-50);
    color: var(--epfl-gray-600);
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    font-size: 0.75rem;
    font-family: inherit;
    cursor: pointer;
  }

  .bulk-btn:hover:not(:disabled) {
    background: var(--epfl-gray-100);
  }

  .bulk-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .bulk-btn.danger:not(:disabled) {
    color: var(--epfl-danger-text);
    border-color: var(--epfl-danger-border);
  }

  .bulk-btn.danger:hover:not(:disabled) {
    background: var(--epfl-danger-bg);
  }

  .move-select {
    padding: 0.3rem 0.4rem;
    background: var(--epfl-gray-50);
    color: var(--epfl-gray-700);
    border: 1px solid var(--epfl-gray-200);
    border-radius: var(--epfl-radius);
    font-size: 0.75rem;
    font-family: inherit;
    cursor: pointer;
    max-width: 11rem;
  }

  .doc-tree {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border-radius: var(--epfl-radius);
  }

  .doc-tree.root-drop {
    outline: 2px dashed var(--epfl-accent);
    outline-offset: 2px;
    background: var(--epfl-gray-100);
  }

  .drag-hint {
    margin: 0;
    padding: 0.3rem 0.5rem;
    font-size: 0.72rem;
    color: var(--epfl-accent);
    background: var(--epfl-accent-bg);
    border-radius: var(--epfl-radius);
  }

  .folder {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border-radius: var(--epfl-radius);
  }

  .folder.drop-target {
    outline: 2px dashed var(--epfl-accent);
    outline-offset: 1px;
    background: var(--epfl-gray-100);
  }

  .folder.dragging-src > .folder-row {
    opacity: 0.4;
  }

  .folder-row {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.4rem;
    border-radius: var(--epfl-radius);
    background: var(--epfl-gray-50);
    border: 1px solid var(--epfl-gray-100);
  }

  .chevron {
    background: none;
    border: none;
    color: var(--epfl-gray-600);
    cursor: pointer;
    font-size: 0.7rem;
    padding: 0 0.15rem;
    line-height: 1;
  }

  .folder-name {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    background: none;
    border: none;
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: 600;
    color: var(--epfl-gray-700);
    cursor: pointer;
    text-align: left;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .folder-count {
    font-weight: 400;
    font-size: 0.7rem;
    color: var(--epfl-gray-400);
  }

  .folder-children {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-left: 0.85rem;
    padding-left: 0.5rem;
    border-left: 1px solid var(--epfl-gray-100);
  }

  .doc-row-wrap {
    display: flex;
    align-items: flex-start;
    gap: 0.25rem;
    padding: 0.5rem;
    border-radius: var(--epfl-radius);
    background: var(--epfl-white);
    border: 1px solid var(--epfl-gray-100);
  }

  .doc-row-wrap.active {
    border-color: var(--epfl-accent);
    background: var(--epfl-gray-50);
  }

  .doc-row-wrap.dragging {
    opacity: 0.4;
  }

  .grip {
    color: var(--epfl-gray-200);
    cursor: grab;
    font-size: 0.95rem;
    line-height: 1;
    padding: 0.15rem 0.25rem;
    align-self: center;
    user-select: none;
    -webkit-user-drag: element;
    touch-action: none;
  }

  .grip:active {
    cursor: grabbing;
  }

  .drag-ghost {
    position: fixed;
    z-index: 1000;
    transform: translate(8px, 8px);
    pointer-events: none;
    max-width: 16rem;
    padding: 0.3rem 0.55rem;
    background: var(--epfl-accent);
    color: var(--epfl-white);
    border-radius: var(--epfl-radius);
    font-size: 0.78rem;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    box-shadow: 0 4px 12px rgba(0, 116, 128, 0.3);
  }

  .doc-row-wrap:hover .grip,
  .folder-row:hover .grip {
    color: var(--epfl-gray-600);
  }

  .doc-row {
    flex: 1;
    display: flex;
    gap: 0.5rem;
    align-items: flex-start;
    cursor: pointer;
  }

  .doc-info {
    flex: 1;
    min-width: 0;
  }

  .doc-name {
    font-size: 0.85rem;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .doc-meta {
    font-size: 0.7rem;
    color: var(--epfl-gray-400);
    margin-top: 0.15rem;
  }

  .delete {
    background: none;
    border: none;
    color: var(--epfl-gray-400);
    font-size: 1.1rem;
    cursor: pointer;
    padding: 0 0.4rem;
  }

  .delete:hover {
    color: var(--epfl-danger);
  }

  .empty {
    font-size: 0.85rem;
    color: var(--epfl-gray-400);
    text-align: center;
    padding: 1.5rem 0.5rem;
  }

  .bypass-row {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.45rem 0.55rem;
    background: var(--epfl-gray-50);
    border: 1px solid var(--epfl-gray-100);
    border-radius: var(--epfl-radius);
    font-size: 0.78rem;
    color: var(--epfl-gray-700);
    cursor: pointer;
    position: relative;
  }

  .bypass-row input[type="checkbox"] {
    cursor: pointer;
  }

  .bypass-row input[type="checkbox"]:disabled {
    cursor: not-allowed;
  }

  .bypass-row input[type="checkbox"]:disabled + .bypass-label {
    color: var(--epfl-gray-400);
    cursor: not-allowed;
  }

  .bypass-label {
    flex: 1;
    line-height: 1.3;
  }

  .info-icon {
    color: var(--epfl-gray-600);
    font-size: 0.85rem;
    margin-left: 0.2rem;
  }

  .size-estimate {
    font-size: 0.72rem;
    font-variant-numeric: tabular-nums;
    color: var(--epfl-gray-600);
    background: var(--epfl-white);
    border: 1px solid var(--epfl-gray-100);
    padding: 0.1rem 0.35rem;
    border-radius: var(--epfl-radius);
    white-space: nowrap;
  }

  .size-estimate.warn-soft {
    color: var(--epfl-warning-text);
    border-color: var(--epfl-warning);
    background: var(--epfl-warning-bg);
  }

  .size-estimate.warn-hard {
    color: var(--epfl-danger-text);
    border-color: var(--epfl-danger-border);
    background: var(--epfl-danger-bg);
  }

  .size-warn {
    margin: 0;
    padding: 0.45rem 0.6rem;
    border-radius: var(--epfl-radius);
    font-size: 0.75rem;
    line-height: 1.35;
  }

  .size-warn.soft {
    background: var(--epfl-warning-bg);
    border: 1px solid var(--epfl-warning);
    color: var(--epfl-warning-text);
  }

  .size-warn.hard {
    background: var(--epfl-danger-bg);
    border: 1px solid var(--epfl-danger-border);
    color: var(--epfl-danger-text);
  }

  .tooltip-wrap {
    position: relative;
  }

  .tooltip-wrap .tooltip {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    z-index: 50;
    background: var(--epfl-text);
    color: var(--epfl-white);
    padding: 0.55rem 0.7rem;
    border-radius: var(--epfl-radius);
    font-size: 0.72rem;
    line-height: 1.4;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
    opacity: 0;
    transform: translateY(-2px);
    pointer-events: none;
    transition: opacity 0.12s ease, transform 0.12s ease;
  }

  .tooltip-wrap .tooltip :global(strong) {
    color: var(--epfl-gray-400);
    font-weight: 600;
  }

  .tooltip-wrap .tooltip :global(code) {
    background: rgba(255, 255, 255, 0.12);
    padding: 0 0.25rem;
    border-radius: var(--epfl-radius);
    font-size: 0.95em;
  }

  .tooltip-wrap .tooltip::after {
    content: "";
    position: absolute;
    bottom: 100%;
    left: 1rem;
    border: 5px solid transparent;
    border-bottom-color: var(--epfl-text);
  }

  .tooltip-wrap:hover .tooltip,
  .tooltip-wrap:focus-within .tooltip {
    opacity: 1;
    transform: translateY(0);
  }
</style>
